import React, { useState } from 'react';
import {
  BellIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import moment from 'moment';

import ListBoxPageSize from './listbox_pase_size';
import PrettyPagination from './pretty_pagination';

export default function NotificationsList({
  notifications,
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  handlePageSizeChange,
  handlePageChange,
}) {
  const [expandedNotifications, setExpandedNotifications] = useState({});

  const handleToggleDetails = (id) => {
    setExpandedNotifications(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const getRecipientsCount = (notification) => {
    if (
      notification.employees_details &&
      Array.isArray(notification.employees_details)
    ) {
      return notification.employees_details.length;
    }

    if (
      notification.employees &&
      Array.isArray(notification.employees)
    ) {
      return notification.employees.length;
    }

    return 0;
  };

  return (
    <>
      <div className="border-t border-x border-gray-200 bg-gray-50 p-4 pb-6">
        <div className="flex items-center justify-end">
          <ListBoxPageSize
            value={pageSize}
            onChange={handlePageSizeChange}
          />
        </div>
      </div>

      {notifications.length === 0 ? (
        <div className="rounded-b-lg border border-dashed border-gray-300 bg-gray-50 px-6 py-12 text-center">
          <BellIcon className="mx-auto h-10 w-10 text-gray-300" />

          <p className="mt-3 text-sm font-semibold text-gray-700">
            Nessuna notifica trovata
          </p>

          <p className="mt-1 text-xs text-gray-500">
            Non sono presenti notifiche da visualizzare
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-b-lg border border-gray-200 bg-white">
            <ul role="list" className="divide-y divide-gray-200">
              {notifications.map((notification) => {
                const isExpanded =
                  expandedNotifications[notification.id];

                const recipientsCount =
                  getRecipientsCount(notification);

                return (
                  <li key={notification.id}>
                    {/* Riga principale */}
                    <div
                      onClick={() =>
                        handleToggleDetails(notification.id)
                      }
                      className="group flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-indigo-50"
                    >
                      {/* Icona */}
                      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-indigo-200">
                        <BellIcon className="h-4 w-4 text-indigo-700" />
                      </span>

                      {/* Titolo */}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm uppercase text-gray-700">
                          {notification.title}
                        </p>
                      </div>

                      {/* Contatori */}
                      <div className="hidden flex-shrink-0 items-center gap-4 sm:flex">
                        <span className="text-xs text-gray-500">
                          Inviata a:{' '}
                          <span className="font-semibold text-gray-700">
                            {recipientsCount}
                          </span>
                        </span>

                        <span className="text-xs text-gray-500">
                          Letta:{' '}
                          <span className="font-semibold text-gray-700">
                            {notification.read_by ?? 0}
                          </span>
                        </span>
                      </div>

                      {/* Data */}
                      <time
                        dateTime={notification.created_at}
                        className="hidden flex-shrink-0 text-xs text-gray-500 md:block"
                      >
                        {moment(notification.created_at).format(
                          'DD/MM/YYYY HH:mm'
                        )}
                      </time>

                      {/* Espansione */}
                      <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors group-hover:text-indigo-600">
                        {isExpanded ? (
                          <ChevronUpIcon className="h-4 w-4" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4" />
                        )}
                      </span>
                    </div>

                    {/* Dettaglio */}
                    {isExpanded && (
                      <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-4 sm:pl-15">
                        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                          {/* Messaggio */}
                          <div>
                            <p className="text-xs uppercase tracking-wider text-gray-500">
                              Messaggio
                            </p>

                            <div className="mt-2 whitespace-pre-line break-words text-xs leading-5 text-gray-700">
                              {notification.message || '-'}
                            </div>
                          </div>

                          {/* Destinatari */}
                          <div>
                            <p className="text-xs uppercase tracking-wider text-gray-500">
                              Destinatari ({recipientsCount})
                            </p>

                            {notification.employees_details &&
                            Array.isArray(
                              notification.employees_details
                            ) &&
                            notification.employees_details.length > 0 ? (
                              <div className="mt-2 max-h-40 overflow-y-auto">
                                <ul className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
                                  {notification.employees_details
                                    .slice()
                                    .sort((a, b) =>
                                      (a.cf || '').localeCompare(
                                        b.cf || ''
                                      )
                                    )
                                    .map((employee) => (
                                      <li
                                        key={employee.id}
                                        className="flex min-w-0 items-center text-xs text-gray-700"
                                      >
                                        <span className="mr-2 block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-indigo-600" />

                                        <span className="truncate">
                                          {employee.last_name &&
                                          employee.first_name
                                            ? `${employee.last_name} ${employee.first_name}`
                                            : employee.cf}
                                        </span>
                                      </li>
                                    ))}
                                </ul>
                              </div>
                            ) : (
                              <p className="mt-2 text-xs text-gray-500">
                                Azienda
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Info mobile */}
                        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-gray-200 pt-3 sm:hidden">
                          <span className="text-xs text-gray-500">
                            Inviata a:{' '}
                            <span className="font-semibold text-gray-700">
                              {recipientsCount}
                            </span>
                          </span>

                          <span className="text-xs text-gray-500">
                            Letta:{' '}
                            <span className="font-semibold text-gray-700">
                              {notification.read_by ?? 0}
                            </span>
                          </span>

                          <span className="text-xs text-gray-500">
                            {moment(notification.created_at).format(
                              'DD/MM/YYYY HH:mm'
                            )}
                          </span>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          <PrettyPagination
            page={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onChange={handlePageChange}
            labelSingolare="notifica"
            labelPlurale="notifiche"
            className="py-3 border border-t rounded-lg mt-3 border-gray-200 bg-gray-50"
          />
        </>
      )}
    </>
  );
}